<template>
  <div
    class="team-effectiveness-questionnaire-page container maxWidth-sm text-big"
    v-if="model && hasAlreadyCompletedQuestionnaire != null"
  >
    <div v-if="!hasAlreadyCompletedQuestionnaire">
      <div class="row" style="margin: 20px 0">
        <div class="col-sm-12 text-center">
          <h1 style="margin: 20px; word-break: break-word">
            Team Effectiveness Questionnaire for '{{ model.name }}'
          </h1>
        </div>
      </div>
      <div v-if="error" class="row">
        <div class="col-sm-12 text-center">
          <p class="text-danger">{{ error }}</p>
        </div>
      </div>
      <team-effectiveness-questionnaire @submit="onSubmit" :is-submitting="isSubmitting" />
    </div>
    <div v-else>
      <div class="row">
        <div class="col-sm-4 col-sm-offset-4 text-center" style="padding: 30px">
          <img
            class="img-responsive"
            src="../../assets/team-effectiveness/celebrate.png"
            alt="Congratulations!"
          />
        </div>
      </div>
      <div class="row">
        <div class="col-sm-12 text-center">
          <h3>
            You have already completed your Team Effectiveness Questionnaire for '{{ model.name }}'.
          </h3>
          <p style="margin: 20px 0">Click the button below to access your report.</p>
        </div>
      </div>
      <div class="row">
        <div class="col-sm-12 text-center">
          <a class="btn btn-primary btn-lg" :href="reportUrl">Open Team Effectiveness Report</a>
        </div>
      </div>
      <div class="row">
        <div class="col-sm-12 text-center" style="margin: 20px 0">
          <p>
            If you’d like to complete a Team Effectiveness Questionnaire on a different team, close
            this session and select another team or create a new one from the top of the page.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { debug as d } from "debug";
import axios from "axios";
import { computed, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import {
  TeamApiModel,
  TeamEffectivenessQuestionnaireApiModel,
  TeamEffectivenessQuestionnaireCreationApiModel,
  TeamEffectivenessRatingsApiModel,
  TeamEffectivenessReviewApiModel,
} from "../../api-models";
import { CsCustomRouter } from "../../router/router";
import TeamEffectivenessQuestionnaire from "../../components/team-effectiveness/team-effectiveness-questionnaire.vue";
import {
  getTeamEffectivenessReportTeamId,
  getTeamEffectivenessReportReviewId,
} from "../../components/team-effectiveness/get-team-effectiveness-report-team-id";

const debug = d("cs.TeamEffectivenessQuestionnairePage");

const route = useRoute();
const router = useRouter();

const teamId = computed(() => getTeamEffectivenessReportTeamId(route));
const reviewId = computed(() => getTeamEffectivenessReportReviewId(route));

const model = ref<TeamApiModel | null>(null);
const error = ref<string | null>(null);
const isSubmitting = ref(false);
const hasAlreadyCompletedQuestionnaire = ref<boolean | null>(null);
const openReviewIdForTeam = ref<string | null>(null);

function onSubmit(rawRatings: TeamEffectivenessRatingsApiModel): void {
  isSubmitting.value = true;
  error.value = null;
  const request = {
    reviewId: reviewId.value,
    rawRatings,
  } as TeamEffectivenessQuestionnaireCreationApiModel;
  axios
    .post("/api/teameffectivenessquestionnaire", request)
    .then(() => {
      if ((router as CsCustomRouter).isCurrentRouteInSlidingStack) {
        (router as CsCustomRouter).closeSlidingStack();
      } else {
        // Navigate to team dashboard...
        window.location.href = `/team/dashboard/${teamId.value}`;
      }
    })
    .catch((err) => {
      debug(err);
      isSubmitting.value = false;
      error.value = "There was a problem submitting. Please try again.";
    });
}

const reportUrl = computed(() => `/teameffectivenessreport/view/${reviewId.value}`);

onMounted(async () => {
  if (!reviewId.value) {
    // Find the open review for the team
    const openReviewForTeam = await axios
      .get(`/api/teameffectivenessreview/getallforteam?teamId=${teamId.value}`)
      .then((resp) => {
        const data = resp.data as TeamEffectivenessReviewApiModel[];
        if (data && data.length > 0 && !data[0].completedDate) {
          return data[0];
        }
        return null;
      });
    if (openReviewForTeam) {
      openReviewIdForTeam.value = openReviewForTeam.id;
    } else {
      // No open review for the team... redirect to the highlights page
      router.replace(`/team/effectiveness/${teamId.value}`);
    }
  }

  await axios
    .get(`/api/teameffectivenessquestionnaire/?reviewId=${reviewId.value}`)
    .then((resp) => {
      const data = resp.data as TeamEffectivenessQuestionnaireApiModel | null;
      hasAlreadyCompletedQuestionnaire.value = !!data;
    });

  // Retrieve the team details (including the name...)
  await axios.get(`/api/teams/${teamId.value}`).then((resp) => {
    const data = resp.data as TeamApiModel;
    model.value = data;
  });
});
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="less">
@import (reference) "../../styles/bootstrap/variables.less";

.team-effectiveness-questionnaire-page {
  min-height: 100vh;
  padding-bottom: 100px;
}
</style>
